import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authApi } from "@/services/api";
import type { User } from "@/types";
import { getPermissions, type UserPermissions } from "@/utils/permissions";

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: UserPermissions | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // 새로고침(F5) 시 localStorage의 user를 즉시 낙관적 복원 → getMe 검증 완료 전
  // user=null로 인한 /login race 방지. getMe는 백그라운드로 검증/갱신한다.
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? (JSON.parse(stored) as User) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token"),
  );
  // 복원된 user가 있으면 로딩 스피너 없이 즉시 인증 상태로 표시. user 없을 때만 대기.
  const [isLoading, setIsLoading] = useState(
    () => !localStorage.getItem("user"),
  );

  const permissions = useMemo(
    () => (user ? getPermissions(user.role) : null),
    [user],
  );

  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await authApi.getMe();
          // 가드: 동시 401→refresh 재시도 와중 axios 인터셉터가 빈(undefined) 응답으로
          // resolve하는 엣지케이스가 있다. "성공" getMe라도 data가 없으면 절대 user를
          // 비우지 않는다(이때 비우면 낙관적 복원된 user가 사라져 /login으로 튕긴다).
          // 진짜 인증 실패(refresh까지 만료)는 api 인터셉터의 clearSessionAndRedirect가 전담.
          if (response?.data) {
            setUser(response.data);
            localStorage.setItem("user", JSON.stringify(response.data));
          }
        } catch {
          // access 만료뿐이면 인터셉터가 refresh로 복구 → getMe가 정상 resolve(이 catch 미진입).
          // refresh까지 실패하면 인터셉터가 로그아웃. 여기서 user/token은 건드리지 않는다.
        }
      }
      setIsLoading(false);
    };
    loadUser();
  }, [token]);

  const login = async (username: string, password: string) => {
    const response = await authApi.login({ username, password });
    const newToken = response.data.access_token;
    localStorage.setItem("token", newToken);
    // refresh token 저장 — access(30분) 만료 시 자동 갱신용 (7일)
    if (response.data.refresh_token) {
      localStorage.setItem("refresh_token", response.data.refresh_token);
    }
    setToken(newToken);
    const userResponse = await authApi.getMe();
    setUser(userResponse.data);
    localStorage.setItem("user", JSON.stringify(userResponse.data));
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        permissions,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
